import { InputAdornment, TextField, TextFieldProps, css, styled } from "@mui/material";
import { AnimationEventHandler, ChangeEvent, FC, FocusEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { IconClose, IconVisibility, IconVisibilityOff } from "@/modules/irohana-components/assets/icons";
import { VALIDATE_DEBOUNCE_DELAY } from "@/modules/irohana-components/constants/common";
import { useFormSchema } from "@/modules/irohana-components/elements/Form";
import { useFormField } from "@/modules/irohana-components/elements/FormField/FormField";
import { IconBox } from "@/modules/irohana-components/elements/IconBox";
import { useDebounce } from "@/modules/irohana-components/hooks/useDebounce";
import { useVisible } from "@/modules/irohana-components/hooks/useVisible";
import { createEnterValidateHandler } from "@/modules/irohana-components/utils/form";
import { background, blue, borderColor, divider, lightBlue, text } from "@/modules/irohana-components/theme/colors-new";
import { typography } from "@/modules/irohana-components/theme/typography";
import { rounded } from "@/modules/irohana-components/theme/variables";
import {
  convertCommasToNumber,
  convertNumberToCommas,
  convertNumberToZipCode,
  convertToPhoneNumber,
  convertZipCodeToNumber,
  handleKatakanaToFurigana,
} from "@/modules/irohana-components/utils/input";
import { toNumber } from "@/modules/irohana-components/utils/number";
import {
  convertFullWidthToHalfWidth,
  convertFullWidthToHalfWidthForMatchLicenseNumber,
  convertHalfWidthToFullWidth,
  convertKanaToHiragana,
  sanitizeInput,
} from "@/modules/irohana-components/utils/pieces";
import { PATTERN } from "@/modules/irohana-components/constants/pattern";

export type TInputSize = "md" | "lg";

export type TInputProps = {
  size?: TInputSize;
  readonly?: boolean;
  previousValue?: string | number;
  clearable?: boolean;
  nonControl?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  previous?: boolean;
  pattern?: RegExp;
  cleanWhitespace?: boolean;
  transformToFullWidth?: boolean;
  transformToHalfWidth?: boolean;
  transformToUpperCase?: boolean;
  commasFormat?: boolean;
  nonInteractive?: boolean;
  disableFieldOnChange?: boolean;
  decimalPlaces?: number;
  removeAllSpace?: boolean;
  isRound?: boolean;
  colorPrevious?: string;
} & Omit<TextFieldProps, "size" | "readOnly">;

export const Input: FC<TInputProps> = ({
  size = "md",
  readonly = false,
  previousValue,
  clearable = true,
  nonControl = false,
  endAdornment,
  startAdornment,
  type,
  placeholder = "入力してください",
  cleanWhitespace = true,
  transformToFullWidth,
  transformToHalfWidth,
  commasFormat,
  nonInteractive = false,
  disableFieldOnChange = false,
  decimalPlaces,
  removeAllSpace = false,
  isRound = false,
  colorPrevious = lightBlue[60],
  ...rest
}) => {
  const ref = useRef<HTMLInputElement>();
  const { field, fieldState, setValue } = useFormField(nonControl);
  const { visible, toggle } = useVisible();
  const fieldName = field?.name;
  const fieldsMeta = useFormSchema();

  const [isAutofilled, setIsAutofilled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [innerValue, setInnerValue] = useState<string | null>(null);

  const handleClear = () => {
    if (!ref.current) return;
    ref.current.value = "";
    setInnerValue(null);
    if (!disableFieldOnChange) field?.onChange("");
    field?.onBlur();

    rest.onChange?.({ target: ref.current } as ChangeEvent<HTMLInputElement>);
    rest?.onBlur?.({ target: ref.current } as FocusEvent<HTMLInputElement>);
  };

  const isCommasFormat = commasFormat || (fieldName && fieldsMeta[fieldName]?.type === "number" && commasFormat !== false);
  const isZipCode = Boolean(fieldName && fieldsMeta[fieldName]?.meta?.isZipCode);
  const isPhone = Boolean(fieldName && fieldsMeta[fieldName]?.meta?.isPhone);
  const value = rest.value ?? field?.value;
  const rawValue = innerValue ?? value;

  // Drop any pending local buffer when the field value changes from outside (e.g. form reset/setValue),
  // so a queued debounce commit cannot overwrite the externally-set value.
  useEffect(() => {
    setInnerValue(null);
  }, [value]);

  const displayValue = useMemo(() => {
    if (rawValue == null || rawValue === "") return "";
    const valueStr = String(rawValue);
    if (isCommasFormat) {
      return isFocused ? convertCommasToNumber(valueStr) : convertNumberToCommas(valueStr);
    }
    if (isZipCode) {
      return convertNumberToZipCode(valueStr);
    }
    return rawValue;
  }, [rawValue, isCommasFormat, isZipCode, isFocused]);

  const inputEndAdornment = useMemo(() => {
    if (rawValue || isAutofilled) {
      if (!clearable || readonly || rest.disabled) return endAdornment;
      if (type === "password") {
        return (
          <InputAdornment position="end">
            <IconBox onClick={toggle}>{visible ? <IconVisibilityOff fontSize="20px" /> : <IconVisibility fontSize="20px" />}</IconBox>
          </InputAdornment>
        );
      }
      return (
        <InputAdornment position="end">
          <IconBox onClick={handleClear}>
            <IconClose fontSize="20px" />
          </IconBox>
        </InputAdornment>
      );
    }
    return endAdornment;
  }, [rawValue, clearable, endAdornment, readonly, rest.disabled, visible, isAutofilled]);

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && (field?.value ?? rest.value) == previousValue;
  }, [fieldState?.isDirty, previousValue, value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInnerValue(e.target.value);
    rest.onChange?.(e);
  };

  useDebounce(
    () => {
      if (innerValue === null || innerValue === (value ?? "")) return;
      if (!disableFieldOnChange) {
        // Gate validation for zipcode/phone: while the value is still incomplete, commit it
        // silently via setValue (no validation) so the user isn't shown an error mid-typing;
        // only run the validating onChange once the full pattern matches.
        if (isZipCode && fieldName && setValue) {
          const zipCodeValue = convertZipCodeToNumber(convertFullWidthToHalfWidth(innerValue)) ?? "";
          if (PATTERN.ZIP_CODE.test(zipCodeValue)) {
            field?.onChange?.(innerValue);
          } else {
            setValue(fieldName, innerValue, { shouldDirty: true, shouldValidate: false });
          }
        } else if (isPhone && fieldName && setValue) {
          if (PATTERN.PHONE_WITH_HYPHEN.test(innerValue) || PATTERN.PHONE_WITHOUT_HYPHEN.test(innerValue)) {
            field?.onChange?.(innerValue);
          } else {
            setValue(fieldName, innerValue, { shouldDirty: true, shouldValidate: false });
          }
        } else {
          field?.onChange?.(innerValue);
        }
      }
      setInnerValue(null);
    },
    [innerValue],
    VALIDATE_DEBOUNCE_DELAY,
  );

  const normalizeValue = (rawInput: string): string => {
    let newValue = rawInput;

    if (fieldName && fieldsMeta[fieldName]?.meta?.isEmptyToNull) {
      newValue = sanitizeInput(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.katakanaToHiragana) {
      newValue = convertKanaToHiragana(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isFurigana) {
      newValue = handleKatakanaToFurigana(newValue);
    }
    if ((fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToFullWidth) || transformToFullWidth) {
      newValue = convertHalfWidthToFullWidth(newValue);
    }
    if ((fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToHalfWidth) || transformToHalfWidth) {
      newValue = convertFullWidthToHalfWidth(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToUpperCase) {
      newValue = newValue.toUpperCase();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.removeSpaces) {
      newValue = newValue.replace(/\s+/g, "");
    }
    if (isCommasFormat) {
      newValue = convertNumberToCommas(newValue).toString();
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isZipCode) {
      newValue = convertNumberToZipCode(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.type === "number" && decimalPlaces) {
      if (newValue) {
        if (isRound) {
          newValue = (Math.round((toNumber(newValue) + Number.EPSILON) * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)).toString();
        } else {
          // Truncate (not round) to decimalPlaces. toPrecision(15) strips float noise
          // before truncating (e.g. 8.7*100 = 869.9999… -> 870), avoiding the off-by-one
          // last-digit error of the old *100/100 approach.
          const factor = Math.pow(10, decimalPlaces);
          newValue = (Math.trunc(Number((toNumber(newValue) * factor).toPrecision(15))) / factor).toString();
        }
      }
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToHalfWidthForMatchLicenseNumber) {
      newValue = convertFullWidthToHalfWidthForMatchLicenseNumber(newValue);
    }
    if (fieldName && fieldsMeta[fieldName]?.meta?.isPhone) {
      newValue = convertToPhoneNumber(newValue);
    }

    if (cleanWhitespace) {
      newValue = newValue.replace(/\t/g, (fieldName && fieldsMeta[fieldName]?.meta?.autoTransformToFullWidth) || transformToHalfWidth ? "　" : " ");
      newValue = newValue.replace(/[ ]+/g, " ");
      newValue = newValue.replace(/[　]+/g, "　");
      newValue = newValue.replace(/^[ \u3000]+|[ \u3000]+$/g, "").trim();
    }

    if (removeAllSpace) {
      newValue = newValue.replace(/[\s\u3000]+/g, "");
    }

    return newValue;
  };

  const handleKeyDown = createEnterValidateHandler(() => {
    const input = ref.current;
    if (input) {
      const newValue = normalizeValue(input.value);
      input.value = newValue;
      setInnerValue(null);
      if (!disableFieldOnChange) field?.onChange?.(newValue);
    }
    field?.onBlur();
  });

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setIsFocused(false);
    const input = e.target;
    const newValue = normalizeValue(input.value);

    input.value = newValue;
    setInnerValue(null);
    if (!disableFieldOnChange) {
      field?.onChange?.(newValue);
    }
    field?.onBlur();
    rest?.onBlur?.(e);
    rest.onChange?.(e);
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setIsFocused(true);
    rest?.onFocus?.(e);
  };

  const makeAnimationStartHandler: AnimationEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const target = e.target as HTMLInputElement | null;
    const autofilled = !!target?.matches("*:-webkit-autofill");
    if (e.animationName === "mui-auto-fill") {
      setIsAutofilled(autofilled);
    }

    if (e.animationName === "mui-auto-fill-cancel") {
      setIsAutofilled(autofilled);
    }
  };

  return (
    <StyledInput
      error={Boolean(fieldState?.error)}
      inputSize={size}
      inputProps={{ ref, onAnimationStart: makeAnimationStartHandler }}
      readonly={readonly}
      disabled={readonly}
      previous={isPrevious}
      colorPrevious={colorPrevious}
      InputProps={{
        endAdornment: inputEndAdornment,
        startAdornment,
        style: nonInteractive ? { pointerEvents: "none" } : {},
      }}
      type={type === "password" && visible ? "text" : type}
      placeholder={type === "password" ? "••••••••" : placeholder}
      {...rest}
      {...field}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={(e) => {
        handleKeyDown(e);
        rest.onKeyDown?.(e);
      }}
    />
  );
};

export const DebounceInput: FC<TInputProps> = (props) => {
  const { field } = useFormField(props.nonControl);
  const { pattern, error, onChange } = props;
  const value = field?.value ?? props.value ?? "";

  const [pending, setPending] = useState<string | null>(null);
  const lastEmitted = useRef<string | null>(null);

  useDebounce(
    () => {
      if (pending === null || pending === lastEmitted.current) return;
      lastEmitted.current = pending;
      onChange?.({ target: { value: pending } } as ChangeEvent<HTMLInputElement>);
    },
    [pending],
    VALIDATE_DEBOUNCE_DELAY,
  );

  return <Input {...props} error={error || (pattern ? !pattern.test(String(value)) : undefined)} onChange={(e) => setPending(e.target.value)} />;
};

const options = { shouldForwardProp: (propName: string) => !["inputSize", "previous", "readonly", "colorPrevious"].includes(propName) };

const StyledInput = styled(TextField, options)<{ inputSize: TInputSize; readonly: boolean; previous: boolean; colorPrevious: string }>`
  .MuiInputBase-root {
    position: relative;
    background: ${background.white};
    padding-right: 8px;
    white-space: nowrap;
    input {
      color: ${text.primary};
      caret-color: ${blue[70]};
      &::placeholder {
        opacity: 1;
        color: ${text.tertiary}!important;
      }
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused::after {
      border-color: ${borderColor.orange};
    }
    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${borderColor.error}!important;
      }
    }
    &.Mui-disabled {
      border-color: transparent;
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    ${({ inputSize }) => {
      if (inputSize === "md") return MediumInput;
      if (inputSize === "lg") return LargeInput;
      return MediumInput;
    }};
    ${({ readonly }) => readonly && readonlyInput};
    ${({ previous, colorPrevious }) =>
      previous &&
      css`
        input {
          color: ${colorPrevious};
        }
      `};
  }
`;

const readonlyInput = css`
  background: ${background.disable} !important;
  border-color: transparent !important;
  pointer-events: none;
  -webkit-text-fill-color: ${text.primary};
  input:-webkit-autofill {
    -webkit-background-clip: text;
    box-shadow: inset 0 0 20px 20px ${background.disable};
  }
  input,
  textarea {
    color: ${text.primary}!important;
    -webkit-text-fill-color: ${text.primary};
    &::placeholder {
      opacity: 0;
    }
  }
  fieldset {
    border-color: ${divider.middle}!important;
  }
`;

const MediumInput = css`
  .MuiInputBase-input {
    padding: 6px 0px;
    padding-left: 8px;
    ${css(typography.body14)};
  }
`;

const LargeInput = css`
  .MuiInputBase-input {
    padding: 12px 8px;
    ${css(typography.body14)};
  }
`;
